/**
 * Applies filters to an array of items.
 *
 * @param {Object[]} items - The items to filter
 * @param {Record<string, string>} filters - Map of filterField.key to current value
 * @param {import('../types').FilterFieldConfig[]} filterFields - Filter field configs from adapter
 * @returns {Object[]} Filtered items
 */
export function applyFilters(items, filters, filterFields) {
    return items.filter((item) => {
        return filterFields.every((field) => {
            const value = filters[field.key];
            // Ignore empty/missing filter values
            if (!value || value === '') return true;

            if (field.type === 'text') {
                // Case-insensitive substring match across all targetFields (OR semantics)
                const lowerValue = value.toLowerCase();
                return field.targetFields.some((targetField) => {
                    const fieldValue = item[targetField];
                    if (fieldValue == null) return false;
                    return String(fieldValue).toLowerCase().includes(lowerValue);
                });
            }

            if (field.type === 'multiselect') {
                // value is comma-separated list of selected values
                if (field.filterFn) return field.filterFn(item, value);
                const selectedSet = new Set(value.split(','));
                const targetField = field.targetFields[0];
                return selectedSet.has(item[targetField]);
            }

            if (field.type === 'select' || field.type === 'toggle') {
                // Allow adapter to provide custom match logic
                if (field.filterFn) return field.filterFn(item, value);
                // Exact equality match on targetFields[0]
                const targetField = field.targetFields[0];
                return item[targetField] === value;
            }

            return true;
        });
    });
}

/**
 * Sorts an array of items.
 *
 * @param {Object[]} items - The items to sort
 * @param {string} sortField - Key of the active sort field
 * @param {'asc' | 'desc'} sortDirection - Sort direction
 * @param {import('../types').SortFieldConfig[]} sortFields - Sort field configs from adapter
 * @returns {Object[]} Sorted items (new array)
 */
export function applySort(items, sortField, sortDirection, sortFields) {
    const config = sortFields.find((f) => f.key === sortField);
    if (!config) return [...items];

    return [...items].sort((a, b) => {
        const aVal = config.getValue(a);
        const bVal = config.getValue(b);

        // Null/undefined sort last regardless of direction
        const aNull = aVal == null;
        const bNull = bVal == null;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;

        let comparison;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
        } else {
            comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        }

        return sortDirection === 'desc' ? -comparison : comparison;
    });
}
